import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { ChangeDetectionStrategy, Component, ElementRef, HostListener } from '@angular/core';
import { I18nModule, RoutingService, UrlModule } from '@spartacus/core';
import { Subscription } from 'rxjs';
import { FocusConfig, FormErrorsModule, ICON_TYPE, IconModule, KeyboardFocusModule } from "@spartacus/storefront";
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GenLaunchDialogService } from 'src/app/generac/services/launch-dialog.service';
import { GenLimitedAvailabilityItem, GenRecommendedAlternative } from 'src/app/generac/models/limited-availability.model';
import { getLimitedAvailabilityFooterKeys, getLimitedAvailabilityIntroKey, getLimitedAvailabilityNewQuantity, getLimitedAvailabilityTitleKey, isLimitedAvailabilityCheckoutError, LIMITED_AVAILABILITY_CHECKOUT_CONTINUE, LIMITED_AVAILABILITY_POPUP_CODES, LIMITED_PARTIAL_AVAILABILITY } from 'src/app/generac/utils/limited-availability.utils';

interface LimitedAvailabilityDialogItem {
  sku: string;
  availableInventory?: number;
  recommendedAlternatives?: GenRecommendedAlternative[];
}

@Component({
  selector: 'gen-warning-dialog',
  templateUrl: './warning-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterModule,
    I18nModule,
    UrlModule,
    IconModule,
    FormErrorsModule,
    ReactiveFormsModule,
    FormsModule,
    KeyboardFocusModule,
  ]
})
export class WarningDialogComponent {
  private subscription = new Subscription();
  iconTypes = ICON_TYPE;
  layoutOption: string | undefined;
  removedItems: GenLimitedAvailabilityItem[] = [];
  adjustedItems: GenLimitedAvailabilityItem[] = [];
  message: string;
  limitedAvailability: any;
  isCartUpdated: boolean = false;
  isLimitedAvailabilityCheckout: boolean = false;
  cartEmptied: boolean = false;

  focusConfig: FocusConfig = {
    trap: true,
    block: true,
    autofocus: 'button',
    focusOnEscape: true,
  };

  get hasRemovedItems(): boolean {
    return this.removedItems.length > 0;
  }

  get hasAdjustedItems(): boolean {
    return this.adjustedItems.length > 0;
  }

  get isMixedChange(): boolean {
    return this.hasRemovedItems && this.hasAdjustedItems;
  }

  get hasRecommendedAlternatives(): boolean {
    return this.getRemovedItemsWithRecommendedAlternatives().length > 0;
  }

  @HostListener('click', ['$event'])
  handleClick(event: MouseEvent): void {
    const modalContainer = this.el.nativeElement.querySelector('.cx-modal-content');

    if (!modalContainer?.contains(event.target as Node)) {
      this.close('Cross click');
    }
  }
  constructor(
    protected launchDialogService: GenLaunchDialogService,
    protected el: ElementRef,
    protected routingService: RoutingService,
  ) { }

  ngOnInit(): void {
    this.subscription.add(
      this.launchDialogService.data$.subscribe((data: any) => {
        if (Array.isArray(data)) {
          this.handleNotifications(data);
          return;
        }

        if (data?.limitedAvailability) {
          this.isLimitedAvailabilityCheckout = true;
          this.cartEmptied = data?.cartEmptied;
          this.setLimitedAvailabilityData(data.limitedAvailability);
          return;
        }

        this.message = data?.message;
      })
    );
  }

  private handleNotifications(notifications: any[]): void {
    notifications = this.filterLowerPriorityDuplicateNotifications(notifications);

    this.isCartUpdated =
      notifications.length > 1 &&
      notifications.some(notification =>
        LIMITED_AVAILABILITY_POPUP_CODES.includes(notification.code)
      );

    notifications.forEach(notification => {
      if (notification.code) {
        this.setLimitedAvailabilityData(notification);
      } else {
        this.message = notification.message;
      }
    });
  }

  private filterLowerPriorityDuplicateNotifications(notifications: any[]): any[] {
    if (notifications.length < 2) {
      return notifications;
    }

    const groupBySkuKey = new Map<string, any[]>();

    notifications.forEach(notification => {
      const skus = Array.isArray(notification.skus) && notification.skus.length
        ? notification.skus
        : notification.items?.map((item: any) => item?.sku).filter(Boolean) ?? [];
      const key = Array.from(new Set(skus)).sort().join('|');

      const group = groupBySkuKey.get(key) ?? [];
      group.push(notification);
      groupBySkuKey.set(key, group);
    });

    return notifications.filter(notification => {
      const skus = Array.isArray(notification.skus) && notification.skus.length
        ? notification.skus
        : notification.items?.map((item: any) => item?.sku).filter(Boolean) ?? [];
      const key = Array.from(new Set(skus)).sort().join('|');
      const group = groupBySkuKey.get(key) ?? [];
      const groupHasPreviousQuantity = group.some((groupNotification: any) =>
        Array.isArray(groupNotification.items) && groupNotification.items.some((item: any) => item?.previousQuantity !== undefined)
      );
      const notificationHasPreviousQuantity = Array.isArray(notification.items) && notification.items.some((item: any) => item?.previousQuantity !== undefined);

      return !groupHasPreviousQuantity || notificationHasPreviousQuantity;
    });
  }

  setLimitedAvailabilityData(notification: any): void {
    const items: GenLimitedAvailabilityItem[] = notification.items?.length
      ? notification.items
      : notification.skus?.map((sku: any) => ({
        sku,
        availableInventory: undefined as number | undefined,
      })) ?? [];

    this.limitedAvailability = {
      titleKey: getLimitedAvailabilityTitleKey(notification, this.isCartUpdated),
      introKey: getLimitedAvailabilityIntroKey(notification, this.isCartUpdated),
      footerKeys: getLimitedAvailabilityFooterKeys(notification, this.isCartUpdated),
      items:
        this.limitedAvailability?.items?.length > 0
          ? Array.from(new Map(
            this.limitedAvailability.items.concat(items).map((item: any) => [item.id, item])
          ).values())
          : items,
      showAvailableInventory: notification.code === LIMITED_PARTIAL_AVAILABILITY,
    };

    this.removedItems = isLimitedAvailabilityCheckoutError(notification) ? notification.removedItems :
      [...this.removedItems, ...(this.limitedAvailability?.removedItems || this.limitedAvailability?.items.filter((item: any) => item.availableInventory === 0))];
    this.adjustedItems = isLimitedAvailabilityCheckoutError(notification) ? notification.adjustedItems :
      [...this.adjustedItems, ...(this.limitedAvailability?.adjustedItems ?? this.limitedAvailability?.items.filter((item: any) => item.availableInventory > 0))];
  }

  public close(reason: string): void {
    this.launchDialogService.closeDialog(reason, 'WARNING_DIALOG');
  }

  public trackBySku(_: number, item: LimitedAvailabilityDialogItem): string {
    return item.sku;
  }

  public trackByAlternativeSku(_: number, alternative: GenRecommendedAlternative): string {
    return alternative.sku;
  }

  public getRemovedItemsWithRecommendedAlternatives(): GenLimitedAvailabilityItem[] {
    return this.removedItems.filter(item => item.recommendedAlternatives?.length);
  }

  getAdjustedQuantity(item: GenLimitedAvailabilityItem): number | undefined {
    return getLimitedAvailabilityNewQuantity(item);
  }

  reviewCart(): void {
    this.close('review cart');
    this.routingService.go({ cxRoute: 'cart' });
  }

  continueAndPlaceOrder(): void {
    this.close(LIMITED_AVAILABILITY_CHECKOUT_CONTINUE);
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.close('close dialog');
  }
}
